/**
 * URL Parser utility for HQ application
 * Handles different URL patterns for UAT, Sandbox, and Production environments
 */

import { getCompanyReferer } from './cookieUtils';

/**
 * Custom error class for URL parsing errors
 */
export class UrlParserError extends Error {
    constructor(message, originalError = null) {
        super(message);
        this.name = 'UrlParserError';
        this.originalError = originalError;
    }
}

/**
 * Get the current port for localhost URLs
 * @returns {string} Port string (e.g., ":3000" or empty string for default ports)
 */
const getCurrentPort = () => {
    try {
        // In browser environment, use window.location.port
        if (typeof window !== 'undefined' && window.location) {
            const port = window.location.port;
            return port ? `:${port}` : '';
        }

        // Default fallback
        return ':3000';
    } catch (error) {
        console.warn('Failed to get current port, using default:', error);
        return ':3000';
    }
};

/**
 * Safely get company referer with error handling
 * @returns {string|null} Company referer or null if failed
 */
const getCompanyRefererSafely = () => {
    try {
        return getCompanyReferer();
    } catch (error) {
        console.warn('Failed to get company referer:', error);
        return null;
    }
};

/**
 * Validate environment parameter
 * @param {string} environment - Environment to validate
 * @returns {boolean} True if valid
 */
const isValidEnvironment = (environment) => {
    const validEnvironments = ['local', 'uat', 'sandbox', 'prod'];
    return validEnvironments.includes(environment);
};

/**
 * Parse the current URL to determine environment and client information
 * @returns {Object} Object containing environment, clientName, and isDefaultUrl
 * @throws {UrlParserError} When URL parsing fails
 */
export const parseUrl = () => {
    try {
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            throw new UrlParserError('parseUrl can only be called in a browser environment');
        }

        if (!window.location) {
            throw new UrlParserError('window.location is not available');
        }

        const currentUrl = window.location.href;

        // Validate that we have a valid URL
        if (!currentUrl || currentUrl === 'about:blank' || currentUrl === 'data:') {
            throw new UrlParserError('Invalid or unsupported URL format');
        }

        let url;
        try {
            url = new URL(currentUrl);
        } catch (error) {
            throw new UrlParserError('Failed to parse URL', error);
        }

        const hostname = url.hostname;

        // Validate hostname
        if (!hostname) {
            throw new UrlParserError('Could not determine hostname from URL');
        }

        // Handle localhost development environment
        if (hostname === 'localhost') {
            const companyReferer = getCompanyRefererSafely();
            return {
                environment: 'local',
                clientName: companyReferer,
                isDefaultUrl: !companyReferer,
            };
        }

        // Default URLs
        const defaultUrls = {
            uat: 'uat.hq.ansr.com',
            sandbox: 'sandbox.hq.ansr.com',
            prod: 'hq.ansr.com',
        };

        // Check if it's a default URL
        const isDefaultUrl = Object.values(defaultUrls).includes(hostname);

        if (isDefaultUrl) {
            // Determine environment from default URL
            let environment = 'prod';
            if (hostname.includes('uat')) {
                environment = 'uat';
            } else if (hostname.includes('sandbox')) {
                environment = 'sandbox';
            }

            return {
                environment,
                clientName: null,
                isDefaultUrl: true,
            };
        }

        // Parse client-specific URL
        // Pattern: https://${clientname}.uat.hq.ansr.com/login
        // Pattern: https://${clientname}.sandbox.hq.ansr.com/login
        // Pattern: https://${clientname}.hq.ansr.com/login

        const uatPattern = /^([^.]+)\.uat\.hq\.ansr\.com$/;
        const sandboxPattern = /^([^.]+)\.sandbox\.hq\.ansr\.com$/;
        const prodPattern = /^([^.]+)\.hq\.ansr\.com$/;

        let environment = 'prod';
        let clientName = null;

        if (uatPattern.test(hostname)) {
            environment = 'uat';
            const match = hostname.match(uatPattern);
            clientName = match ? match[1] : null;
        } else if (sandboxPattern.test(hostname)) {
            environment = 'sandbox';
            const match = hostname.match(sandboxPattern);
            clientName = match ? match[1] : null;
        } else if (prodPattern.test(hostname)) {
            environment = 'prod';
            const match = hostname.match(prodPattern);
            clientName = match ? match[1] : null;
        }

        // Validate that we extracted a client name for client-specific URLs
        if (!isDefaultUrl && !clientName) {
            throw new UrlParserError(`Could not extract client name from hostname: ${hostname}`);
        }

        return {
            environment,
            clientName,
            isDefaultUrl: false,
        };
    } catch (error) {
        if (error instanceof UrlParserError) {
            throw error;
        }
        throw new UrlParserError('Unexpected error during URL parsing', error);
    }
};

/**
 * Generate client-specific URL based on environment and client name
 * @param {string} environment - 'local', 'uat', 'sandbox', or 'prod'
 * @param {string} clientName - Client name
 * @param {string} path - Path to append (default: '/login')
 * @returns {string} Generated URL
 * @throws {UrlParserError} When parameters are invalid or URL generation fails
 */
export const generateClientUrl = (environment, clientName, path = '/login') => {
    try {
        // Validate parameters
        if (!environment) {
            throw new UrlParserError('Environment parameter is required');
        }

        if (!isValidEnvironment(environment)) {
            throw new UrlParserError(`Invalid environment: ${environment}. Must be one of: local, uat, sandbox, prod`);
        }

        if (!clientName) {
            throw new UrlParserError('Client name is required to generate client-specific URL');
        }

        if (typeof clientName !== 'string' || clientName.trim() === '') {
            throw new UrlParserError('Client name must be a non-empty string');
        }

        // Sanitize client name (remove any potentially dangerous characters)
        const sanitizedClientName = clientName
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '');

        if (sanitizedClientName === '') {
            throw new UrlParserError('Client name contains no valid characters after sanitization');
        }

        // Validate path
        if (path && typeof path !== 'string') {
            throw new UrlParserError('Path must be a string');
        }

        const normalizedPath = path || '/login';

        // Handle localhost environment
        if (environment === 'local') {
            const port = getCurrentPort();
            return `http://localhost${port}${normalizedPath}`;
        }

        const baseUrls = {
            uat: `${sanitizedClientName}.uat.hq.ansr.com`,
            sandbox: `${sanitizedClientName}.sandbox.hq.ansr.com`,
            prod: `${sanitizedClientName}.hq.ansr.com`,
        };

        const baseUrl = baseUrls[environment];
        if (!baseUrl) {
            throw new UrlParserError(`Invalid environment: ${environment}`);
        }

        return `https://${baseUrl}${normalizedPath}`;
    } catch (error) {
        if (error instanceof UrlParserError) {
            throw error;
        }
        throw new UrlParserError('Unexpected error during client URL generation', error);
    }
};

/**
 * Generate default URL based on environment
 * @param {string} environment - 'local', 'uat', 'sandbox', or 'prod'
 * @param {string} path - Path to append (default: '/login')
 * @returns {string} Generated default URL
 * @throws {UrlParserError} When environment is invalid or URL generation fails
 */
export const generateDefaultUrl = (environment, path = '/login') => {
    try {
        // Validate parameters
        if (!environment) {
            throw new UrlParserError('Environment parameter is required');
        }

        if (!isValidEnvironment(environment)) {
            throw new UrlParserError(`Invalid environment: ${environment}. Must be one of: local, uat, sandbox, prod`);
        }

        // Validate path
        if (path && typeof path !== 'string') {
            throw new UrlParserError('Path must be a string');
        }

        const normalizedPath = path || '/login';

        // Handle localhost environment
        if (environment === 'local') {
            const port = getCurrentPort();
            return `http://localhost${port}${normalizedPath}`;
        }

        const defaultUrls = {
            uat: 'uat.hq.ansr.com',
            sandbox: 'sandbox.hq.ansr.com',
            prod: 'hq.ansr.com',
        };

        const baseUrl = defaultUrls[environment];
        if (!baseUrl) {
            throw new UrlParserError(`Invalid environment: ${environment}`);
        }

        return `https://${baseUrl}${normalizedPath}`;
    } catch (error) {
        if (error instanceof UrlParserError) {
            throw error;
        }
        throw new UrlParserError('Unexpected error during default URL generation', error);
    }
};

/**
 * Generate referer URL for API requests
 * @param {string} companyReferer - Company referer slug
 * @returns {string|null} Generated referer URL or null if companyReferer is not provided
 * @throws {UrlParserError} When URL generation fails
 */
export const generateRefererUrl = (companyReferer) => {
    try {
        if (!companyReferer) {
            return null;
        }

        if (typeof companyReferer !== 'string' || companyReferer.trim() === '') {
            throw new UrlParserError('Company referer must be a non-empty string');
        }

        // Sanitize company referer
        const sanitizedReferer = companyReferer
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '');

        if (sanitizedReferer === '') {
            throw new UrlParserError('Company referer contains no valid characters after sanitization');
        }

        // Check if we're in a browser environment
        if (typeof window === 'undefined' || !window.location) {
            // Fallback to production environment when window.location is not available
            return `https://${sanitizedReferer}.hq.ansr.com/dashboard`;
        }

        const hostname = window.location.hostname;

        if (!hostname) {
            throw new UrlParserError('Could not determine hostname for referer URL generation');
        }

        // Handle different environments
        if (hostname === 'localhost' || hostname.includes('.uat.hq.ansr.com')) {
            return `https://${sanitizedReferer}.uat.hq.ansr.com/dashboard`;
        } else if (hostname.includes('.sandbox.hq.ansr.com')) {
            return `https://${sanitizedReferer}.sandbox.hq.ansr.com/dashboard`;
        } else if (hostname.includes('.hq.ansr.com')) {
            return `https://${sanitizedReferer}.hq.ansr.com/dashboard`;
        }

        // Default fallback
        return `https://${sanitizedReferer}.hq.ansr.com/dashboard`;
    } catch (error) {
        if (error instanceof UrlParserError) {
            throw error;
        }
        throw new UrlParserError('Unexpected error during referer URL generation', error);
    }
};
